import {Injectable} from '@angular/core';
import {environment as env} from '@patientlynk3/environment';
import {HttpClient} from '@angular/common/http';
import {FeatureCollection} from 'geojson';
import {Observable} from 'rxjs';
import { share, shareReplay } from 'rxjs/operators';
import { LabVisit, LabVisitMetadata, RecordList } from '@patientlynk3/services/labs/lab.model';
import { Profile } from '@patientlynk3/models/profile.model';


@Injectable({
  providedIn: 'root'
})
export class ApiService {
  constructor(private httpClient: HttpClient) { }
  // TODO: abstract httpClient methods, apiUrl appending, database appending

  private API = {
    get: <T = any>(endpoint = '', options = {}, db = 'default') =>
      this.httpClient.get<T>(`${env.apiUrl}${endpoint}`, options),
    post: <T = any>(endpoint, data = {}, db = 'default', options = {}) =>
      this.httpClient.post<T>(`${env.apiUrl}${endpoint}`, data, options),
    delete: <T = any>(endpoint, data = {}, db = 'default', options = {}) =>
      this.httpClient.delete<T>(`${env.apiUrl}${endpoint}`, options),
    patch: <T = any>(endpoint, data = {}, db = 'default', options = {}) =>
      this.httpClient.patch<Partial<T>>(`${env.apiUrl}${endpoint}`, data, options),
  };

  auth = {
    login: data => this.API.post(`/patientlynk/login/`, data),
    logout: ({ access: access_token, refresh: refresh_token }) =>
      this.API.post(`/abartys_logout/`, { access_token, refresh_token }),
    refresh: data => this.API.post(`/api/token/refresh/`, data, 'default', {
      headers: { ignoreLoadingBar: '' }
    }),
  };

  public getProfile(db = 'default'): Observable<Profile> {
    return this.httpClient.post<Profile>(`${env.apiUrl}/patientlynk/members/profile/`, { database: db });
  }

  public editProfile(profile, id) {
    return this.httpClient.patch(`${env.apiUrl}/patientlynk/members/${id}/?database=default`, profile, {
      headers: {
        ignoreLoadingBar: ''
      }
    });
  }

  public getWebPackage(variant, appVersion){
    return this.httpClient.post(`https://t20i7k4z2g.execute-api.us-east-1.amazonaws.com/prod/check_update`, {variant: variant, bundle_version: appVersion});
  }

  public getMemberEmployers (){
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/member_employers/?database=default`);
  }

  public getDatabases() {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/databases/`);
  }

  public mailbox(db, id) {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/files/insurance_documents/?database=${db}&person=${id}`);
  }

  public getHRA() {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/surveys/?database=default`);
  }

  public getHraAnswers(userId) {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/surveyResponses_answers/?person=${userId}&database=default`);
  }

  public submitHraForm(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/hra/surveyResponses/?database=default`, data);
  }

  public HRAMetrics(selectedRecord) {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/hra_metrics/?database=default&survey_response_id=${selectedRecord}`);
  }

  public new_hra_api = {
    specific_surveys: (id) => this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/surveys/${id}/?database=default`),
    specific_answers: (id) => this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/surveyResponses_answers/${id}/?database=default`),
    surveys: (current) => this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/surveys/?database=default&current=${current}`),
    answers: (current, select_list) => this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/surveyResponses_answers/?database=default&current=${current}&select_list=${select_list}`)
  }

  public risk_report = {
    modifiable_risk_graph: (selectedRecord) => this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/modifiable_risk_graph/?database=default&survey_response_id=${selectedRecord}`),
    cvd_breakdown_risk_graph: (selectedRecord) => this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/cvd_breakdown_risk_graph/?database=default&survey_response_id=${selectedRecord}`),
    diabetes_breakdown_risk_graph: (selectedRecord) => this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/diabetes_breakdown_risk_graph/?database=default&survey_response_id=${selectedRecord}`)
  }

  public modifiableRiskTable(selectedRecord) {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/hra/modifiable_risk_table/?database=default&survey_response_id=${selectedRecord.id}`);
  }

  public get_health_records(id = null){
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/files/health_records/?database=default`);
  }

  public delete_health_records(file){
    return this.httpClient.delete(`${env.apiUrl}/patientlynk/members/files/health_records/${file.id}/?database=default`);
  }

  public edit_health_records(file) {
    return this.httpClient.patch(`${env.apiUrl}/patientlynk/members/files/health_records/${file.id}/?database=default`, file);
  }

  public upload_health_records(formData){
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/files/health_records/?database=default`, formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  public mfa = {
    status: () =>{
      return this.httpClient.get(`${env.apiUrl}/mfa/`);
    },
    activate: (request) => {
      return this.httpClient.put(`${env.apiUrl}/mfa/${request.type}/request/`, request.data);
    },
    confirm: (request) => {
      return this.httpClient.put(`${env.apiUrl}/mfa/${request.type}/verify/`, request.data);
    },
    remove_mfa: () => {
      return this.httpClient.delete(`${env.apiUrl}/mfa/`);
    },
    recovery_code: () => {
      return this.httpClient.get(`${env.apiUrl}/mfa/recovery/`);
    }
  }

  public map = {
    municipalities: () => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/municipalities/`),
    countries: () => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/countries/`),
    subdivisions: (country) => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/countries/subdivisions/?country=${country}`),
    officeDetail: (office, db) => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/office_detail/${office}/?database=${db}`),
    networks: (db) => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/networks/?insurance=${db}&database=${db}`),
    specialties: (db, networks) => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/specialties/?insurance=${db}&networks=${networks}`),
    internal: (params) => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/internal/`, params),
    public: (params) => this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/public/`, params),
    providerMissing: (params) => this.httpClient.post(`${env.apiUrl}/utils/palig_provider_missing/`, params),
  }


  public terms = {
    obtain:()=> this.httpClient.get(`${env.apiUrl}/patientlynk/users/terms/?database=default`),
    accept:(data: any) => this.httpClient.post(`${env.apiUrl}/patientlynk/users/versioned_terms_and_conditions/?database=default`, data),
  }

  public getTerms() {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/providers/versioned_terms_and_conditions/?database=default`);
  }

  public preRegistration(data: { email: string, password1: string, password2: string }): Observable<any> {
    return this.API.post(`/patientlynk/pre_registration/`, data);
  }

  public register(data: any) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/registration/`, data);
  }

  public activate_account(data: any) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/activate_account/`, data);
  }

  //setup for test user
  public create_test_user(data: any){
    return this.httpClient.post(`${env.apiUrl}/lis/v1/patientlynk/create_account/`, data)
  }

  public accountConfirm(data: any) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/account-confirm-email/`, data);
  }

  public listOfLabs() {
    return this.httpClient.get<any[]>(`${env.apiUrl}/patientlynk/members/lab_results/list_laboratories/`);
  }

  public listRecords() {
    return this.httpClient.get<RecordList>(`${env.apiUrl}/patientlynk/members/lab_results/list_records/`);
  }

  public deleteRecord(id: string) {
    return this.httpClient.delete(`${env.apiUrl}/patientlynk/members/lab_results/${id}/delete/`);
  }

  public listOfLabsObscured() {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/lab_results/hidden_results/`);
  }

  public revealLabsObscured(data){
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/lab_results/validate_result/`, data);
  }

  public dismissLab(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/lab_results/dismiss_result/`, data);
  }

  public listResults(date_start?: string | '', date_end?: string | '', read?: boolean | null) {
    return this.httpClient.get<LabVisitMetadata[]>(`${env.apiUrl}/patientlynk/members/lab_results/list_lab_results/?date_start=${date_start}&date_end=${date_end}&read=${read}`);
  }

  public getOtherLabSourcesCount() {
    return this.httpClient.get<Record<string, number>>(`${env.apiUrl}/patientlynk/members/lab_results/other_sources/`);
  }

  public getDataTrends(person_id, panel_id, loinc_code, date_start, date_end) {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/lab_results/get_data_trends/?person_id=${person_id}&panel_id=${panel_id}&loinc_code=${loinc_code}&date_start=${date_start}&date_end=${date_end}`)
  } 

  public shareLabResult(data) {
    return this.API.post(`/patientlynk/members/lab_results/share/`, data);
  }

  public getSharedLabResult(key, downloadPDF: 0 | 1 = 0) {
    // separate query params for readability
    const params = `?key=${key}&pdf_response=${downloadPDF}`;

    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/lab_results/obtain_shared_lab_results/${params}`,
      downloadPDF && {responseType: 'blob'}); // conditionally change response type when retrieving raw PDF
  }

  public getSharedLabResultPDF(key, downloadPDF: 0 | 1 = 0) {
    // separate query params for readability
    const params = `?key=${key}&pdf_response=${downloadPDF}`;

    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/lab_results/obtain_shared_lab_results/${params}`, {observe: 'response', responseType: 'blob'}); // conditionally change response type when retrieving raw PDF
  }

  public getLabResult(labID, date, downloadPDF: 0 | 1 = 0): Observable<LabVisit | Blob> {
    // separate query params for readability
    const params = `?lab_record_id=${labID}&results_id=${date}&pdf_response=${downloadPDF}`;

    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/lab_results/obtain_lab_results/${params}`,
      downloadPDF && {responseType: 'blob'}); // conditionally change response type when retrieving raw PDF
  }

  public getLabResultPDF(labID, date, downloadPDF: 0 | 1 = 0){
    // separate query params for readability
    const params = `?lab_record_id=${labID}&results_id=${date}&pdf_response=${downloadPDF}`;

    return this.httpClient.get(`${env.apiUrl}/patientlynk/members/lab_results/obtain_lab_results/${params}`, {observe: 'response', responseType: 'blob'}); // conditionally change response type when retrieving raw PDF
  }


  public addResultsById(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/lab_results/add_record/?database=default`, data);
  }

  public addResultsByPhone(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/lab_results/send_phone_verification/`, data);
  }

  public updateSMSNotifications(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/lab_results/sms_notify/?database=default`, data)
  }

  public registerPushNotification(user_device){
    return this.httpClient.post(`${env.apiUrl}/utils/register_push_notifications/?database=default`, user_device).pipe(shareReplay(1))
  }

  public taxonomy(){
    return this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/taxonomies/`);
  }

  public provider_search(query_params){
    return this.httpClient.get(`${env.apiUrl}/insurelynk/providers/map/global_public_map_search/?${query_params}`);
  }

  public saveCard(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/health_plan/cards/?database=default`, data);
  }

  public editCard(data, id) {
    return this.httpClient.patch(`${env.apiUrl}/patientlynk/members/health_plan/cards/${id}/?database=default`, data);
  }

  public deleteCard(id) {
    return this.httpClient.delete(`${env.apiUrl}/patientlynk/members/health_plan/cards/${id}/?database=default`);
  }

  public contact(data) {
    return this.httpClient.post(`${env.apiUrl}/utils/email_contact/`, data);
  }

  public contact_labinfo(data) {
    return this.httpClient.post(`${env.apiUrl}/utils/labinfo_problem_report/`, data);
  }

  public resendCode(data) {
    return this.httpClient.post( `${env.apiUrl}/patientlynk/resend_verification_code/`, data);
  }

  public checkLabsByCode(data) {
    return this.httpClient.post( `${env.apiUrl}/patientlynk/members/lab_results/validate_phone_add_record/`, data);
  }

  public emailRecovery(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/recovery/send_email/`, data);
  }

  public sendVerificationCode(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/recovery/send_mfa_verification_code/`, data);
  }

  public validateRecoveryMFA(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/recovery/validate_mfa_verification_code/`, data);
  }

  public resetPassword(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/recovery/reset_password/`, data);
  }

  public acceptNewTerms(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/users/terms_acceptance/?database=default`, data)
  }

  public getTermsStatus() {
    return this.httpClient.get(`${env.apiUrl}/patientlynk/users/terms_acceptance/?database=default`)
  }

  public patchTerms(terms_id, data) {
    return this.httpClient.patch(`${env.apiUrl}/patientlynk/users/terms_acceptance/${terms_id}/?database=default`, data)
  }

  public emailTerms(data) {
    return this.httpClient.post(`${env.apiUrl}/utils/email_patientlynk_terms/`, data);
  }

  public deleteHealthCards(data) {
    return this.httpClient.post(`${env.apiUrl}/patientlynk/members/health_plan/cards/batch_delete/`, data);
  }

  passWordReset(old_password: string, new_password1: string, new_password2: string) {
    return this.httpClient.post(`${env.apiUrl}/rest-auth/password/change/`, { old_password, new_password1, new_password2 });
  }

  public getSurveyStatus() {
    return this.API.get(`/patientlynk/members/symptom_survey/`);
  }

  public saveSurveyStatus(data) {
    return this.API.post(`/patientlynk/members/symptom_survey/`, data);
  }

  public getStreamToken() {
    return this.API.get(`/utils/stream_token/`);
  }

  public getCarriers() {
    return this.API.get(`/patientlynk/members/health_plan/cards/carriers/`);
  }

  public enrichment(data) {
    return this.API.post(`/utils/notification_enrichment/`, data);
  }

  public labinfoMapData() {
    return this.API.get<FeatureCollection>(`/patientlynk/providers/maps/labinfo/`);
  }

  createDependent = data => this.API.post(`/patientlynk/members/family/`, data);
  deleteDependent = id => this.API.delete(`/patientlynk/members/family/${id}/`);
  editDependent = (changes, id) => this.API.patch(`/patientlynk/members/family/${id}/`, changes);

  checkZip = zip => this.API.post(`/patientlynk/providers/maps/find_zipcode/`, { zip });

  // New API endpoint configurations
  getCountries = () => this.API.get(`/utils/get_countries/`);
  getStates = (countryCode) => this.API.get(`/utils/get_subdivisions/?country=${countryCode}`);
  getCities = (countryCode, divisionName) => this.API.get(`/utils/get_municipalities/?country=${countryCode}&subdivision=${divisionName}`);
  getSectors = (countryCode, divisionName, cityName) => this.API.get(`/utils/get_sectors/?country=${countryCode}&subdivision=${divisionName}&municipality=${cityName}`);

}
